import { create } from 'zustand'
import { persist } from 'zustand/middleware'

interface CollectionTreeState {
  expandedCollections: Set<string>
  expandedFolders: Set<string>
  version: number
}

interface CollectionTreeStateStore {
  state: CollectionTreeState
  
  isCollectionExpanded: (collectionId: string) => boolean
  isFolderExpanded: (folderId: string) => boolean
  
  toggleCollection: (collectionId: string) => void
  toggleFolder: (folderId: string) => void
  
  toggleCollectionWithChildren: (
    collectionId: string, 
    subcollectionIds: string[], 
    folderIds: string[]
  ) => void
  
  toggleFolderWithChildren: (
    folderId: string,
    subfolderIds: string[]
  ) => void
  
  setCollectionExpanded: (collectionId: string, expanded: boolean) => void
  setFolderExpanded: (folderId: string, expanded: boolean) => void
  
  expandAllCollections: () => void
  collapseAllCollections: () => void
  
  clearExpansionState: () => void
  
  batchSetExpanded: (collections: string[], folders: string[]) => void
  
  cleanupDeletedItems: (existingCollectionIds: string[], existingFolderIds: string[]) => void
}

const CURRENT_VERSION = 1

const defaultState: CollectionTreeState = {
  expandedCollections: new Set<string>(),
  expandedFolders: new Set<string>(),
  version: CURRENT_VERSION,
}

export const useCollectionTreeStateStore = create<CollectionTreeStateStore>()(
  persist(
    (set, get) => ({
      state: defaultState,
      
      isCollectionExpanded: (collectionId: string) => {
        const { state } = get()
        return state.expandedCollections.has(collectionId)
      },
      
      isFolderExpanded: (folderId: string) => {
        const { state } = get()
        return state.expandedFolders.has(folderId)
      },
      
      toggleCollection: (collectionId: string) => {
        set((store) => {
          const newExpanded = new Set(store.state.expandedCollections)
          if (newExpanded.has(collectionId)) {
            newExpanded.delete(collectionId)
          } else {
            newExpanded.add(collectionId)
          }
          return {
            state: {
              ...store.state,
              expandedCollections: newExpanded,
              version: CURRENT_VERSION,
            },
          }
        })
      },
      
      toggleFolder: (folderId: string) => {
        set((store) => {
          const newExpanded = new Set(store.state.expandedFolders)
          if (newExpanded.has(folderId)) {
            newExpanded.delete(folderId)
          } else {
            newExpanded.add(folderId)
          }
          return {
            state: {
              ...store.state,
              expandedFolders: newExpanded,
              version: CURRENT_VERSION,
            },
          }
        })
      },
      
      toggleCollectionWithChildren: (
        collectionId: string, 
        subcollectionIds: string[], 
        folderIds: string[]
      ) => {
        set((store) => {
          const newExpandedCollections = new Set(store.state.expandedCollections)
          const newExpandedFolders = new Set(store.state.expandedFolders)
          
          if (newExpandedCollections.has(collectionId)) {
            // Collapsing: remove the collection and all its children
            newExpandedCollections.delete(collectionId)
            
            // Remove all subcollections
            for (const subcollectionId of subcollectionIds) {
              newExpandedCollections.delete(subcollectionId)
            }
            
            // Remove all folders
            for (const folderId of folderIds) {
              newExpandedFolders.delete(folderId)
            }
          } else {
            // Expanding: just add the collection itself
            newExpandedCollections.add(collectionId)
          }
          
          return {
            state: {
              expandedCollections: newExpandedCollections,
              expandedFolders: newExpandedFolders,
              version: CURRENT_VERSION,
            },
          }
        })
      },
      
      toggleFolderWithChildren: (
        folderId: string,
        subfolderIds: string[]
      ) => {
        set((store) => {
          const newExpanded = new Set(store.state.expandedFolders)
          
          if (newExpanded.has(folderId)) {
            // Collapsing: remove the folder and all its subfolders
            newExpanded.delete(folderId)
            
            // Remove all subfolders
            for (const subfolderId of subfolderIds) {
              newExpanded.delete(subfolderId)
            }
          } else {
            // Expanding: just add the folder itself
            newExpanded.add(folderId)
          }
          
          return {
            state: {
              ...store.state,
              expandedFolders: newExpanded,
              version: CURRENT_VERSION,
            },
          }
        })
      },
      
      setCollectionExpanded: (collectionId: string, expanded: boolean) => {
        set((store) => {
          const newExpanded = new Set(store.state.expandedCollections)
          if (expanded) {
            newExpanded.add(collectionId)
          } else {
            newExpanded.delete(collectionId)
          }
          return {
            state: {
              ...store.state,
              expandedCollections: newExpanded,
              version: CURRENT_VERSION,
            },
          }
        })
      },
      
      setFolderExpanded: (folderId: string, expanded: boolean) => {
        set((store) => {
          const newExpanded = new Set(store.state.expandedFolders)
          if (expanded) {
            newExpanded.add(folderId)
          } else {
            newExpanded.delete(folderId)
          }
          return {
            state: {
              ...store.state,
              expandedFolders: newExpanded,
              version: CURRENT_VERSION,
            },
          }
        })
      },
      
      expandAllCollections: () => {
        set((store) => ({
          state: {
            ...store.state,
            expandedCollections: new Set(store.state.expandedCollections),
            version: CURRENT_VERSION,
          },
        }))
      },
      
      collapseAllCollections: () => {
        set((store) => ({
          state: {
            ...store.state,
            expandedCollections: new Set<string>(),
            expandedFolders: new Set<string>(),
            version: CURRENT_VERSION,
          },
        }))
      },
      
      clearExpansionState: () => {
        set({
          state: {
            expandedCollections: new Set<string>(),
            expandedFolders: new Set<string>(),
            version: CURRENT_VERSION,
          },
        })
      },
      
      batchSetExpanded: (collections: string[], folders: string[]) => {
        set({
          state: {
            expandedCollections: new Set(collections),
            expandedFolders: new Set(folders),
            version: CURRENT_VERSION,
          },
        })
      },
      
      cleanupDeletedItems: (existingCollectionIds: string[], existingFolderIds: string[]) => {
        set((store) => {
          const existingCollectionsSet = new Set(existingCollectionIds)
          const existingFoldersSet = new Set(existingFolderIds)
          
          const cleanedCollections = new Set(
            Array.from(store.state.expandedCollections).filter(id => existingCollectionsSet.has(id))
          )
          const cleanedFolders = new Set(
            Array.from(store.state.expandedFolders).filter(id => existingFoldersSet.has(id))
          )
          
          return {
            state: {
              expandedCollections: cleanedCollections,
              expandedFolders: cleanedFolders,
              version: CURRENT_VERSION,
            },
          }
        })
      },
    }),
    {
      name: 'collection-tree-state',
      storage: {
        getItem: (name) => {
          try {
            const str = localStorage.getItem(name)
            if (!str) return null
            
            const stored: unknown = JSON.parse(str)
            if (!stored) return null
            
            const versionCandidate = (typeof stored === 'object' && stored !== null && 'state' in stored && typeof (stored as { state?: unknown }).state === 'object')
              ? ((stored as { state?: { version?: unknown } }).state?.version)
              : undefined
            const version = typeof versionCandidate === 'number' ? versionCandidate : 0
            
            if (version < CURRENT_VERSION) {
              // Migration needed - reset state
              return null
            }
            
            if (typeof stored === 'object' && stored !== null && 'state' in stored) {
              const sUnknown = (stored as { state?: unknown }).state
              const s = (sUnknown && typeof sUnknown === 'object') ? sUnknown as Partial<CollectionTreeState> : {}
              return {
                ...(stored as Record<string, unknown>),
                state: {
                  expandedCollections: new Set(Array.isArray(s?.expandedCollections) ? (s.expandedCollections as unknown[]).filter((x): x is string => typeof x === 'string') : []),
                  expandedFolders: new Set(Array.isArray(s?.expandedFolders) ? (s.expandedFolders as unknown[]).filter((x): x is string => typeof x === 'string') : []),
                  version: typeof s?.version === 'number' ? s.version : CURRENT_VERSION,
                },
              }
            }
            return null
          } catch (error) {
            console.error('Failed to load collection tree state:', error)
            return null
          }
        },
        setItem: (name, value) => {
          try {
            const typedValue = value as unknown
            const candidateState = (typedValue && typeof typedValue === 'object' && 'state' in (typedValue as { state?: unknown }))
              ? (typedValue as { state?: unknown }).state
              : undefined
            const toStringArray = (val: unknown): string[] => {
              if (val instanceof Set) {
                return Array.from(val).filter((x): x is string => typeof x === 'string')
              }
              if (Array.isArray(val)) {
                return (val as unknown[]).filter((x): x is string => typeof x === 'string')
              }
              return []
            }

            const s: CollectionTreeState = (candidateState && typeof candidateState === 'object')
              ? {
                  expandedCollections: new Set<string>(toStringArray((candidateState as Partial<CollectionTreeState>).expandedCollections)),
                  expandedFolders: new Set<string>(toStringArray((candidateState as Partial<CollectionTreeState>).expandedFolders)),
                  version: typeof (candidateState as Partial<CollectionTreeState>).version === 'number' 
                    ? (candidateState as Partial<CollectionTreeState>).version as number
                    : CURRENT_VERSION,
                }
              : defaultState
            const toStore = {
              state: {
                expandedCollections: Array.from(s.expandedCollections || []),
                expandedFolders: Array.from(s.expandedFolders || []),
                version: s.version || CURRENT_VERSION,
              },
            }
            localStorage.setItem(name, JSON.stringify(toStore))
          } catch (error) {
            console.error('Failed to save collection tree state:', error)
          }
        },
        removeItem: (name) => {
          try {
            localStorage.removeItem(name)
          } catch (error) {
            console.error('Failed to remove collection tree state:', error)
          }
        },
      },
    }
  )
)